import { refreshAdmin } from './admin-auth-api';
import { useAdminAuthStore } from '../stores/useAdminAuthStore';

export type AvatarFactoryLayerKey = 'soul' | 'style' | 'memory' | 'skill';

export interface AvatarFactoryLayer {
  layerKey: AvatarFactoryLayerKey;
  content: string;
  evidenceJson: Array<Record<string, unknown>>;
  reviewNotesJson: string[];
  generationStatus: string;
}

export interface AvatarFactorySourceDocument {
  id: string;
  sourceKind: string;
  filename: string;
  contentType: string;
  originalSizeBytes: number;
  conversionStatus: string;
  markdownText: string;
  diagnosticsJson: Record<string, unknown>;
  errorMessage: string | null;
}

export interface AvatarFactoryVersion {
  id: string;
  name: string;
  createdAt: string;
}

export interface AvatarFactoryPreviewMessage {
  id: string;
  role: string;
  content: string;
  messageKind: string;
  metadataJson: Record<string, unknown>;
  createdAt: string;
}

export interface AvatarFactoryPublishResult {
  profileId: string;
  artifactId: string;
  artifactVersion: string;
  bindingRevision: number;
  runtimeRevision: number;
  storageUri: string;
  webAgentProfile: string;
  wechatEntrypointProfileId: string | null;
}

export interface AvatarFactoryDraftDetail {
  id: string;
  displayName: string;
  description: string | null;
  greeting: string;
  scheduleConfigJson: Record<string, unknown>;
  sourceStatus: string;
  generationStatus: string;
  activeVersionId: string | null;
  previewRevision: number;
  sources: AvatarFactorySourceDocument[];
  layers: AvatarFactoryLayer[];
  versions: AvatarFactoryVersion[];
  previewMessages: AvatarFactoryPreviewMessage[];
  createdAt: string;
  updatedAt: string;
}

export interface AvatarFactoryDraftPatch {
  displayName?: string;
  description?: string | null;
  greeting?: string;
  scheduleConfigJson?: Record<string, unknown>;
  layers?: Partial<Record<AvatarFactoryLayerKey, string>>;
}

export interface StreamFactoryPreviewInput {
  message: string;
  onText: (content: string) => void;
  onError: (message: string) => void;
  onDone: () => void;
  signal?: AbortSignal;
}

interface ApiAvatarFactoryLayer {
  layer_key: AvatarFactoryLayerKey;
  content: string;
  evidence_json: Array<Record<string, unknown>>;
  review_notes_json: string[];
  generation_status: string;
}

interface ApiAvatarFactorySourceDocument {
  id: string;
  source_kind: string;
  filename: string;
  content_type: string;
  original_size_bytes: number;
  conversion_status: string;
  markdown_text: string;
  diagnostics_json: Record<string, unknown>;
  error_message: string | null;
}

interface ApiAvatarFactoryVersion {
  id: string;
  name: string;
  created_at: string;
}

interface ApiAvatarFactoryPreviewMessage {
  id: string;
  role: string;
  content: string;
  message_kind: string;
  metadata_json: Record<string, unknown>;
  created_at: string;
}

interface ApiAvatarFactoryPublishResult {
  profile_id: string;
  artifact_id: string;
  artifact_version: string;
  binding_revision: number;
  runtime_revision: number;
  storage_uri: string;
  web_agent_profile: string;
  wechat_entrypoint_profile_id: string | null;
}

interface ApiAvatarFactoryDraftDetail {
  id: string;
  display_name: string;
  description: string | null;
  greeting: string;
  schedule_config_json: Record<string, unknown>;
  source_status: string;
  generation_status: string;
  active_version_id: string | null;
  preview_revision: number;
  sources: ApiAvatarFactorySourceDocument[];
  layers: ApiAvatarFactoryLayer[];
  versions: ApiAvatarFactoryVersion[];
  preview_messages: ApiAvatarFactoryPreviewMessage[];
  created_at: string;
  updated_at: string;
}

let adminSessionRefreshPromise: Promise<string | null> | null = null;

async function readErrorMessage(response: Response): Promise<string> {
  const fallback = response.statusText || 'Request failed.';
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    try {
      const payload = (await response.json()) as { detail?: unknown };
      if (typeof payload.detail === 'string' && payload.detail.trim()) {
        return payload.detail;
      }
      if (payload.detail != null) {
        return JSON.stringify(payload.detail);
      }
      return JSON.stringify(payload);
    } catch {
      return fallback;
    }
  }

  const text = await response.text().catch(() => fallback);
  return text || fallback;
}

async function refreshAdminAccessToken(): Promise<string | null> {
  if (adminSessionRefreshPromise) {
    return adminSessionRefreshPromise;
  }

  adminSessionRefreshPromise = refreshAdmin()
    .then((session) => {
      useAdminAuthStore.getState().setSession(session.accessToken, session.user);
      return session.accessToken;
    })
    .catch(() => {
      useAdminAuthStore.getState().clearSession();
      return null;
    })
    .finally(() => {
      adminSessionRefreshPromise = null;
    });

  return adminSessionRefreshPromise;
}

function sendAdminRequest(
  token: string,
  input: RequestInfo | URL,
  init: RequestInit,
): Promise<Response> {
  return fetch(input, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.headers ?? {}),
    },
  });
}

async function sendAdminRequestWithRefresh(
  accessToken: string,
  input: RequestInfo | URL,
  init: RequestInit,
): Promise<Response> {
  let response = await sendAdminRequest(accessToken, input, init);

  if (response.status === 401) {
    const refreshedAccessToken = await refreshAdminAccessToken();
    if (refreshedAccessToken) {
      response = await sendAdminRequest(refreshedAccessToken, input, init);
    }
  }

  return response;
}

async function requestAdminAvatarFactory<T>(
  accessToken: string,
  input: RequestInfo | URL,
  init: RequestInit,
  actionLabel: string,
): Promise<T> {
  const response = await sendAdminRequestWithRefresh(accessToken, input, init);

  if (!response.ok) {
    const message = await readErrorMessage(response);
    throw new Error(`${actionLabel} failed (${response.status}): ${message}`);
  }

  return response.json() as Promise<T>;
}

function normalizeLayer(payload: ApiAvatarFactoryLayer): AvatarFactoryLayer {
  return {
    layerKey: payload.layer_key,
    content: payload.content,
    evidenceJson: payload.evidence_json,
    reviewNotesJson: payload.review_notes_json,
    generationStatus: payload.generation_status,
  };
}

function normalizeSourceDocument(payload: ApiAvatarFactorySourceDocument): AvatarFactorySourceDocument {
  return {
    id: payload.id,
    sourceKind: payload.source_kind,
    filename: payload.filename,
    contentType: payload.content_type,
    originalSizeBytes: payload.original_size_bytes,
    conversionStatus: payload.conversion_status,
    markdownText: payload.markdown_text,
    diagnosticsJson: payload.diagnostics_json,
    errorMessage: payload.error_message,
  };
}

function normalizeVersion(payload: ApiAvatarFactoryVersion): AvatarFactoryVersion {
  return {
    id: payload.id,
    name: payload.name,
    createdAt: payload.created_at,
  };
}

function normalizePreviewMessage(payload: ApiAvatarFactoryPreviewMessage): AvatarFactoryPreviewMessage {
  return {
    id: payload.id,
    role: payload.role,
    content: payload.content,
    messageKind: payload.message_kind,
    metadataJson: payload.metadata_json,
    createdAt: payload.created_at,
  };
}

function normalizePublishResult(payload: ApiAvatarFactoryPublishResult): AvatarFactoryPublishResult {
  return {
    profileId: payload.profile_id,
    artifactId: payload.artifact_id,
    artifactVersion: payload.artifact_version,
    bindingRevision: payload.binding_revision,
    runtimeRevision: payload.runtime_revision,
    storageUri: payload.storage_uri,
    webAgentProfile: payload.web_agent_profile,
    wechatEntrypointProfileId: payload.wechat_entrypoint_profile_id,
  };
}

function normalizeDraft(payload: ApiAvatarFactoryDraftDetail): AvatarFactoryDraftDetail {
  return {
    id: payload.id,
    displayName: payload.display_name,
    description: payload.description,
    greeting: payload.greeting,
    scheduleConfigJson: payload.schedule_config_json,
    sourceStatus: payload.source_status,
    generationStatus: payload.generation_status,
    activeVersionId: payload.active_version_id,
    previewRevision: payload.preview_revision,
    sources: payload.sources.map(normalizeSourceDocument),
    layers: payload.layers.map(normalizeLayer),
    versions: payload.versions.map(normalizeVersion),
    previewMessages: payload.preview_messages.map(normalizePreviewMessage),
    createdAt: payload.created_at,
    updatedAt: payload.updated_at,
  };
}

export async function getCurrentAvatarFactoryDraft(accessToken: string): Promise<AvatarFactoryDraftDetail> {
  const payload = await requestAdminAvatarFactory<ApiAvatarFactoryDraftDetail>(
    accessToken,
    '/api/v1/admin/avatar-factory/drafts/current',
    { method: 'GET' },
    'Load Houmao clone factory draft',
  );
  return normalizeDraft(payload);
}

export async function createAvatarFactoryDraft(
  accessToken: string,
  input: { displayName: string },
): Promise<AvatarFactoryDraftDetail> {
  const payload = await requestAdminAvatarFactory<ApiAvatarFactoryDraftDetail>(
    accessToken,
    '/api/v1/admin/avatar-factory/drafts',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        display_name: input.displayName,
      }),
    },
    'Create Houmao clone factory draft',
  );
  return normalizeDraft(payload);
}

export async function updateAvatarFactoryDraft(
  accessToken: string,
  draftId: string,
  patch: AvatarFactoryDraftPatch,
): Promise<AvatarFactoryDraftDetail> {
  const payload: Record<string, unknown> = {};

  if (patch.displayName !== undefined) payload.display_name = patch.displayName;
  if (patch.description !== undefined) payload.description = patch.description;
  if (patch.greeting !== undefined) payload.greeting = patch.greeting;
  if (patch.scheduleConfigJson !== undefined) payload.schedule_config_json = patch.scheduleConfigJson;
  if (patch.layers !== undefined) payload.layers = patch.layers;

  const response = await requestAdminAvatarFactory<ApiAvatarFactoryDraftDetail>(
    accessToken,
    `/api/v1/admin/avatar-factory/drafts/${draftId}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    'Update Houmao clone factory draft',
  );

  return normalizeDraft(response);
}

export async function uploadAvatarFactorySource(
  accessToken: string,
  draftId: string,
  input: { file?: File; files?: File[]; pastedText?: string },
): Promise<AvatarFactoryDraftDetail> {
  const formData = new FormData();

  const files = input.files ?? (input.file ? [input.file] : []);
  for (const file of files) {
    formData.append('files', file);
  }
  if (input.pastedText) {
    formData.append('pasted_text', input.pastedText);
  }

  const payload = await requestAdminAvatarFactory<ApiAvatarFactoryDraftDetail>(
    accessToken,
    `/api/v1/admin/avatar-factory/drafts/${draftId}/sources`,
    {
      method: 'POST',
      body: formData,
    },
    'Upload Houmao clone source',
  );

  return normalizeDraft(payload);
}

export async function regenerateAvatarFactoryDraft(
  accessToken: string,
  draftId: string,
): Promise<AvatarFactoryDraftDetail> {
  const payload = await requestAdminAvatarFactory<ApiAvatarFactoryDraftDetail>(
    accessToken,
    `/api/v1/admin/avatar-factory/drafts/${draftId}/generate`,
    { method: 'POST' },
    'Generate Houmao clone draft',
  );
  return normalizeDraft(payload);
}

export const generateAvatarFactoryDraft = regenerateAvatarFactoryDraft;

export async function saveAvatarFactoryVersion(
  accessToken: string,
  draftId: string,
  name: string,
): Promise<AvatarFactoryDraftDetail> {
  const payload = await requestAdminAvatarFactory<ApiAvatarFactoryDraftDetail>(
    accessToken,
    `/api/v1/admin/avatar-factory/drafts/${draftId}/versions`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    },
    'Save Houmao clone version',
  );
  return normalizeDraft(payload);
}

export async function restoreAvatarFactoryVersion(
  accessToken: string,
  draftId: string,
  versionId: string,
): Promise<AvatarFactoryDraftDetail> {
  const payload = await requestAdminAvatarFactory<ApiAvatarFactoryDraftDetail>(
    accessToken,
    `/api/v1/admin/avatar-factory/drafts/${draftId}/versions/${versionId}/restore`,
    { method: 'POST' },
    'Restore Houmao clone version',
  );
  return normalizeDraft(payload);
}

export async function publishAvatarFactoryDraft(
  accessToken: string,
  draftId: string,
  input: { profileId?: string; setWechatEntrypoint?: boolean } = {},
): Promise<AvatarFactoryPublishResult> {
  const payload = await requestAdminAvatarFactory<ApiAvatarFactoryPublishResult>(
    accessToken,
    `/api/v1/admin/avatar-factory/drafts/${draftId}/publish`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        profile_id: input.profileId,
        set_wechat_entrypoint: input.setWechatEntrypoint ?? false,
      }),
    },
    'Publish Houmao clone draft',
  );

  return normalizePublishResult(payload);
}

export async function streamAvatarFactoryPreviewChat(
  accessToken: string,
  draftId: string,
  input: StreamFactoryPreviewInput,
): Promise<void> {
  try {
    const response = await sendAdminRequestWithRefresh(
      accessToken,
      `/api/v1/admin/avatar-factory/drafts/${draftId}/preview-chat`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: input.message }),
        signal: input.signal,
      },
    );

    if (!response.ok) {
      const message = await readErrorMessage(response);
      input.onError(`Preview chat failed (${response.status}): ${message}`);
      return;
    }

    if (!response.body) {
      input.onError('Preview chat stream did not include a response body.');
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let lineBuffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      lineBuffer += decoder.decode(value, { stream: true });
      const lines = lineBuffer.split('\n');
      lineBuffer = lines.pop() ?? '';

      for (const line of lines) {
        const isDone = parsePreviewSseLine(line, input);
        if (isDone) {
          return;
        }
      }
    }

    const flush = decoder.decode();
    if (flush) {
      lineBuffer += flush;
    }

    if (lineBuffer.trim()) {
      parsePreviewSseLine(lineBuffer, input);
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return;
    }
    input.onError(error instanceof Error ? error.message : String(error));
  }
}

function parsePreviewSseLine(line: string, input: StreamFactoryPreviewInput): boolean {
  if (!line.startsWith('data:')) {
    return false;
  }

  const data = line.slice(5).trim();
  if (!data) {
    return false;
  }

  if (data === '[DONE]') {
    input.onDone();
    return true;
  }

  try {
    const payload = JSON.parse(data) as {
      event?: string;
      content?: unknown;
      message?: unknown;
    };

    if (payload.event === 'text' && typeof payload.content === 'string') {
      input.onText(payload.content);
    } else if (payload.event === 'error') {
      if (typeof payload.message === 'string' && payload.message.trim()) {
        input.onError(payload.message);
      } else {
        input.onError('Preview stream returned an error event.');
      }
    }
  } catch {
    // Ignore malformed SSE lines.
  }

  return false;
}

export async function simulateAvatarFactorySchedule(
  accessToken: string,
  draftId: string,
): Promise<AvatarFactoryPreviewMessage> {
  const payload = await requestAdminAvatarFactory<ApiAvatarFactoryPreviewMessage>(
    accessToken,
    `/api/v1/admin/avatar-factory/drafts/${draftId}/simulate-schedule`,
    { method: 'POST' },
    'Simulate Houmao clone schedule',
  );

  return normalizePreviewMessage(payload);
}
